import { gql } from '@apollo/client';

export const getProgrammesList = gql`
  query Programme {
    programmes: programme(
      order_by: { updated_at: desc }
      where: { active: { _eq: true } }
    ) {
      id
      title
      active
      code
      units {
        id
        unit_code
        created_at
        updated_at
        title
        e_learning_id
        assets
        outcomes {
          id
          programme_unit_id
          title
          description
          type
          overall: overall_outcome
          group: outcome_group
        }
      }
      created_at
      updated_at
      cohorts {
        id
        event_id
        due_dates
        studentAggregate: members_aggregate(
          where: { type: { _eq: "STUDENT" } }
        ) {
          aggregate {
            count
          }
        }
        team: members(
          where: {
            _or: [
              { type: { _eq: "TUTOR" } }
              { type: { _eq: "ASSESSOR" } }
              { type: { _eq: "IQA" } }
            ]
          }
        ) {
          id
          type
          member {
            id
          }
        }
      }
    }
  }
`;

export const getProgrammesStudents = gql`
  query Programme {
    programmes: programme(
      where: { cohorts: { standard: { _eq: true } } }
      order_by: { updated_at: desc }
    ) {
      id
      title
      active
      code
      created_at
      updated_at
      cohorts(where: { standard: { _eq: true } }) {
        id
        standard
      }
    }
  }
`;

export const getProgrammesNav = gql`
  query Programme {
    programmes: programme(order_by: { updated_at: desc }) {
      id
      title
      active
    }
  }
`;

export const getActionPlans = gql`
  query getActionPlans(
    $limit: Int!
    $offset: Int!
    $where: form_response_bool_exp!
  ) {
    actionPlans: form_response(
      order_by: { updatedAt: desc }
      limit: $limit
      offset: $offset
      where: $where
    ) {
      id
      createdAt
      updatedAt
      complete
      completed_at
      completed_by_employee
      client_complete
      client_completed_at
      responses
      asset
      resolution_date
      resolution_status
      resolution_user
      form: formByForm {
        id
        title
        type
      }
      user: userByUser {
        id
        full_name
        given_name
        family_name
      }
    }
    actionPlansCount: form_response_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

export const updateActionPlanResolution = gql`
  mutation updateActionPlanResolution(
    $id: uuid!
    $resolution_status: String
    $resolution_user: uuid
    $resolution_date: date
  ) {
    update_form_response_by_pk(
      pk_columns: { id: $id }
      _set: {
        resolution_status: $resolution_status
        resolution_user: $resolution_user
        resolution_date: $resolution_date
      }
    ) {
      id
      resolution_status
      resolution_user
      resolution_date
    }
  }
`;

export const getProgrammesWithUnits = gql`
  query Programme($where: programme_bool_exp) {
    programmes: programme(order_by: { updated_at: desc }, where: $where) {
      id
      title
      active
      itec
      units {
        id
        title
        unit_code
        hasExam
        exams(order_by: { created_at: asc }) {
          id
          title
          active
          is_default
        }
      }
    }
  }
`;

export const programmes = gql`
  query Programme {
    programmes: programme {
      id
      title
      active
      code
      units {
        id
        unit_code
        created_at
        updated_at
        title
        e_learning_id
        assets
        outcomes {
          id
          title
          description
          type
          overall: overall_outcome
          group: outcome_group
        }
      }
      created_at
      updated_at
      cohorts {
        id
        event_id
        due_dates
        students: members(
          where: { _and: [{ type: { _eq: "STUDENT" } }] }
          order_by: { created_at: desc }
        ) {
          id
          created_at
          updated_at
          status
          cohort_id
          cohort {
            team: members(
              where: {
                _or: [
                  { type: { _eq: "TUTOR" } }
                  { type: { _eq: "ASSESSOR" } }
                  { type: { _eq: "IQA" } }
                ]
              }
            ) {
              id
              type
              member {
                id
              }
            }
            event_id
          }
          member {
            id
            name: given_name
            surname: family_name
          }
        }
        team: members(
          where: {
            _or: [
              { type: { _eq: "TUTOR" } }
              { type: { _eq: "ASSESSOR" } }
              { type: { _eq: "IQA" } }
            ]
          }
        ) {
          id
          type
          member {
            id
          }
        }
      }
    }
  }
`;

export const insertProgramme = gql`
  mutation CreateProgramme($data: programme_insert_input!) {
    insert_programme_one(object: $data) {
      id
    }
  }
`;

export const updateEvidence = gql`
  mutation updateEvidence($id: uuid!, $data: evidence_set_input!) {
    evidence: update_evidence_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const getUnitEvidence = gql`
  query getUnitEvidence($unitId: uuid!) {
    assignments: evidence(
      where: {
        _and: [{ unit_id: { _eq: $unitId } }, { assignment: { _eq: true } }]
      }
    ) {
      id
      submitted
      submitted_at
      created_at
      updated_at
      asset_id
      report_id
      revision
      report {
        id
        created_at
      }
      form_id
      form {
        id
        created_at
        form: formByForm {
          title
        }
      }
      cohort {
        due_dates
        students: members(
          where: { _and: [{ type: { _eq: "STUDENT" } }] }
          order_by: { created_at: desc }
        ) {
          id
          created_at
          updated_at
          status
          member {
            id
            full_name
          }
        }
        team: members(
          where: {
            _or: [
              { type: { _eq: "TUTOR" } }
              { type: { _eq: "ASSESSOR" } }
              { type: { _eq: "IQA" } }
            ]
          }
        ) {
          id
          created_at
          updated_at
          type
          status
          member {
            id
            full_name
            avatar: user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
      }
      unit {
        unit_code
        title
      }
      asset {
        id
        url
        props
      }
      owner {
        id
        full_name
      }
      statuses {
        status
      }
      outcomes {
        id
        outcome {
          id
          title
          description
          type
          overall: overall_outcome
          group: outcome_group
          outcomes {
            id
            title
            description
            type
            markings {
              id
              created_at
              updated_at
              outcome_id
              member_id
              evidence_id
              user {
                id
                full_name
              }
            }
            overall: overall_outcome
            group: outcome_group
          }
        }
      }
    }
    evidence(
      where: {
        _and: [{ unit_id: { _eq: $unitId } }, { assignment: { _eq: false } }]
      }
    ) {
      id
      submitted
      submitted_at
      created_at
      updated_at
      asset_id
      report_id
      report {
        id
        created_at
      }
      form_id
      form {
        id
        created_at
        form: formByForm {
          title
        }
      }
      cohort {
        due_dates
        students: members(
          where: { _and: [{ type: { _eq: "STUDENT" } }] }
          order_by: { created_at: desc }
        ) {
          id
          created_at
          updated_at
          status
          member {
            id
            full_name
          }
        }
        team: members(
          where: {
            _or: [
              { type: { _eq: "TUTOR" } }
              { type: { _eq: "ASSESSOR" } }
              { type: { _eq: "IQA" } }
            ]
          }
        ) {
          id
          created_at
          updated_at
          type
          status
          member {
            id
            full_name
            avatar: user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
      }
      unit {
        unit_code
        title
      }
      asset {
        id
        url
        props
      }
      owner {
        id
        full_name
      }
      statuses {
        status
      }
      outcomes {
        id
        outcome {
          id
          title
          description
          type
          overall: overall_outcome
          group: outcome_group
          outcomes {
            id
            title
            description
            type
            markings {
              id
              created_at
              updated_at
              outcome_id
              member_id
              evidence_id
            }
            overall: overall_outcome
            group: outcome_group
          }
        }
      }
    }
  }
`;

export const getUnit = gql`
  query Unit($id: uuid!) {
    unit: programme_unit_by_pk(id: $id) {
      id
      created_at
      updated_at
      title
      description
      active
      unit_code
      e_learning_id
      hasExam
      exams(order_by: { created_at: asc }) {
        id
        title
        active
        is_default
      }
      assets
      programme_id
      programme {
        id
        title
        active
        code
        qualification_number
        created_at
        updated_at
      }
      outcomes {
        id
        title
        description
        group: outcome_group
        overall: overall_outcome
        type
        evidence {
          id
          created_at
          updated_at
          evidence {
            submitted
            submitted_at
            asset_id
            asset {
              id
              url
              props
            }
            statuses {
              status
            }
          }
        }
      }
    }
  }
`;

export const createUnitExam = gql`
  mutation CreateUnitExam($unitId: uuid!, $title: String!) {
    insert_programme_unit_exam_one(
      object: { programme_unit_id: $unitId, title: $title }
    ) {
      id
    }
  }
`;

export const updateUnitExam = gql`
  mutation UpdateUnitExam($id: uuid!, $data: programme_unit_exam_set_input!) {
    update_programme_unit_exam_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const getProgramme = gql`
  query Programme($id: uuid!) {
    programme: programme_by_pk(id: $id) {
      id
      title
      active
      code
      level
      qualification_number
      created_at
      updated_at
      units {
        id
        created_at
        updated_at
        title
        description
        active
        unit_code
        e_learning_id
        assets
        assignment
        assignment_template
        outcomes(order_by: { created_at: asc }) {
          id
          title
          description
          outcome_group
          overall_outcome
          type
          assignment_options
        }
        evidence {
          id
          archived
          submitted
          submitted_at
          created_at
          updated_at
          asset_id
          owner {
            id
            full_name
          }
        }
      }
      cohorts {
        id
        due_dates
        name
        status
        created_by
        created_at
        updated_at
        event_id
        createdBy {
          id
          full_name
        }
        event {
          id
          start
          end
          detail {
            title
          }
        }
        studentAggregate: members_aggregate(
          where: { type: { _eq: "STUDENT" } }
        ) {
          aggregate {
            count
          }
        }
        team: members(
          where: {
            _or: [
              { type: { _eq: "TUTOR" } }
              { type: { _eq: "ASSESSOR" } }
              { type: { _eq: "IQA" } }
            ]
          }
        ) {
          id
          created_at
          updated_at
          type
          member {
            id
            full_name
            avatar: user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
        students: members(where: { _or: [{ type: { _eq: "STUDENT" } }] }) {
          id
          created_at
          updated_at
          member {
            id
            full_name
          }
        }
      }
    }
  }
`;

export const notifications = gql`
  query notifications($id: uuid!) {
    cohorts: cohort(where: { members: { member: { id: { _eq: $id } } } }) {
      id
      name
      status
      created_at
      programme {
        title
      }
      evidence {
        id
        submitted
        submitted_at
        created_at
        updated_at
        asset_id
        unit {
          title
        }
        owner {
          id
          full_name
        }
      }
      students: members(
        where: { _and: [{ type: { _eq: "STUDENT" } }] }
        limit: 5
        order_by: { created_at: desc }
      ) {
        id
        created_at
        updated_at
        status
        member {
          id
          full_name
        }
        addedBy {
          id
          full_name
        }
      }
      team: members(
        where: {
          _or: [
            { type: { _eq: "TUTOR" } }
            { type: { _eq: "ASSESSOR" } }
            { type: { _eq: "IQA" } }
          ]
        }
        limit: 5
        order_by: { created_at: desc }
      ) {
        id
        created_at
        member {
          id
          full_name
          avatar: user_assets {
            asset: assetByAsset {
              id
              url
            }
          }
        }
      }
    }
  }
`;

export const yourCohorts = gql`
  query yourCohorts($userId: uuid!) {
    cohorts: cohort(where: { members: { member_id: { _eq: $userId } } }) {
      id
      name
      status
      created_at
      updated_at
      event {
        id
        title
        detail {
          title
        }
        end
        start
      }
      students: members(where: { _or: [{ type: { _eq: "STUDENT" } }] }) {
        id
        created_at
        updated_at
        status
        member {
          id
          full_name
        }
        addedBy {
          id
          full_name
        }
      }
      team: members(
        where: {
          _or: [
            { type: { _eq: "TUTOR" } }
            { type: { _eq: "ASSESSOR" } }
            { type: { _eq: "IQA" } }
          ]
        }
      ) {
        id
        member {
          id
          full_name
          avatar: user_assets {
            asset: assetByAsset {
              id
              url
            }
          }
        }
      }
      programme {
        id
        title
      }
      createdBy {
        id
        full_name
      }
    }
  }
`;

export const cohorts = gql`
  query Cohorts($where: cohort_bool_exp) {
    cohorts: cohort(where: $where, order_by: { created_at: desc }) {
      id
      name
      status
      created_at
      updated_at
      event {
        id
        title
        detail {
          title
        }
        end
        start
      }
      students_aggregate: members_aggregate(
        where: { _or: [{ type: { _eq: "STUDENT" } }] }
      ) {
        aggregate {
          count
        }
      }
      team_aggregate: members_aggregate(
        where: {
          _or: [
            { type: { _eq: "TUTOR" } }
            { type: { _eq: "ASSESSOR" } }
            { type: { _eq: "IQA" } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
      programme {
        id
        title
      }
      createdBy {
        id
        full_name
      }
    }
  }
`;

export const insertCohort = gql`
  mutation insertCohort($data: cohort_insert_input!) {
    cohort: insert_cohort_one(object: $data) {
      id
    }
  }
`;

export const updateCohort = gql`
  mutation updateCohort($id: uuid!, $data: cohort_set_input!) {
    cohort: update_cohort_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const updateCohortMember = gql`
  mutation updateCohortMember($id: uuid!, $data: cohort_member_set_input!) {
    cohort: update_cohort_member_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const getCohortMemberExamGate = gql`
  query getCohortMemberExamGate($userId: uuid!, $programmeId: uuid!) {
    members: cohort_member(
      where: {
        _and: [
          { member_id: { _eq: $userId } }
          { type: { _eq: "STUDENT" } }
          { archived: { _eq: false } }
          { cohort: { programme_id: { _eq: $programmeId } } }
        ]
      }
      limit: 1
    ) {
      id
      exam_scheduling_approved_at
      member {
        id
        vtct_learner_code
      }
    }
  }
`;

export const updateCohortMembersWhere = gql`
  mutation updateCohortMembersWhere(
    $where: cohort_member_bool_exp!
    $data: cohort_member_set_input!
  ) {
    update_cohort_member(where: $where, _set: $data) {
      affected_rows
      returning {
        id
        event_id
      }
    }
  }
`;

export const getUserCohortMembers = gql`
  query getUserCohortMembers($userId: uuid!) {
    cohortMembers: cohort_member(
      where: {
        _and: [
          { member_id: { _eq: $userId } }
          { type: { _eq: "STUDENT" } }
          { archived: { _eq: false } }
        ]
      }
      order_by: { created_at: desc }
    ) {
      id
      event_id
      status
      event {
        id
        start
        detail {
          title
        }
        title
      }
      cohort {
        id
        name
        programme {
          id
          title
        }
      }
    }
  }
`;

export const deleteCohortMember = gql`
  mutation deleteCohortMember($id: uuid!) {
    cohortMember: delete_cohort_member_by_pk(id: $id) {
      id
    }
  }
`;

export const deleteCohortMembers = gql`
  mutation deleteCohortMembers($ids: [uuid!]!) {
    cohortMember: delete_cohort_member(where: { id: { _in: $ids } }) {
      affected_rows
    }
  }
`;

export const cohort = gql`
  query Cohort($id: uuid!) {
    cohort: cohort_by_pk(id: $id) {
      id
      name
      created_at
      updated_at
      evidence {
        owner {
          id
          full_name
        }
        revisionBy {
          full_name
        }
        unit {
          id
          title
        }
        assignment
        accepted_at
        accepted
        submitted
        submitted_at
        revision
        revision_at
      }
      due_dates
      notes {
        id
        internal
        note {
          created_at
          id
          title
          content
        }
      }
      team: members(
        where: {
          _or: [
            { type: { _eq: "TUTOR" } }
            { type: { _eq: "ASSESSOR" } }
            { type: { _eq: "IQA" } }
          ]
        }
      ) {
        id
        created_at
        updated_at
        type
        status
        member {
          id
          full_name
          avatar: user_assets {
            asset: assetByAsset {
              id
              url
            }
          }
        }
      }
      students: members(where: { _or: [{ type: { _eq: "STUDENT" } }] }) {
        id
        created_at
        updated_at
        status
        evidence_complete
        member {
          id
          full_name
          evidenceSummary
        }
        addedBy {
          id
          full_name
        }
      }
      event {
        id
        title
        start
        end
        detail {
          title
        }
      }
      programme {
        id
        title
        units {
          id
          created_at
          active
          code: unit_code
          title
          description
          assets
          e_learning_id
          outcomes {
            id
            title
            description
            group: outcome_group
            overall: overall_outcome
            type
          }
        }
      }
    }
  }
`;

export const addCohort = gql`
  mutation CreateCohort($data: cohort_insert_input!) {
    cohort: insert_cohort_one(object: $data) {
      id
    }
  }
`;

export const insertCohortMembers = gql`
  mutation insertCohortMember($data: [cohort_member_insert_input!]!) {
    insert_cohort_member(objects: $data) {
      affected_rows
      returning {
        id
        event_id
        cohort_id
        member_id
      }
    }
  }
`;

export const cohortMembers = gql`
  query CohortMembers($where: cohort_member_bool_exp) {
    members: cohort_member(where: $where) {
      id
      created_at
      updated_at
      type
      status
      cohort {
        id
        name
        status
        due_dates
        programme {
          code
          title
          units {
            id
            created_at
            updated_at
            title
            description
            active
            unit_code
            e_learning_id
            assets
          }
        }
      }
      assignments {
        id
        created_at
        statuses {
          id
          created_at
          status
        }
        unit {
          title
          description
          active
          unit_code
          e_learning_id
          assets
        }
      }
      member {
        id
        evidenceSummary
        full_name
        avatar: user_assets {
          asset: assetByAsset {
            id
            url
          }
        }
      }
    }
  }
`;

export const getCohortMembers = gql`
  query CohortMembers {
    members: user(
      order_by: { given_name: asc }
      where: {
        _and: [
          { is_in_programme: { _eq: true } }
          { cohort_members: { type: { _eq: "STUDENT" } } }
        ]
      }
    ) {
      id
      email
      programmeStatus
      created_at
      updated_at
      full_name
      name: given_name
      surname: family_name
      cohort_members {
        cohort_id
        cohort {
          id
          programme_id
          programme {
            title
          }
          event_id
          team: members(
            where: {
              _or: [
                { type: { _eq: "TUTOR" } }
                { type: { _eq: "ASSESSOR" } }
                { type: { _eq: "IQA" } }
              ]
            }
          ) {
            id
            type
          }
        }
      }
    }
  }
`;

export const getEvidenceOutcomes = gql`
  query getEvidenceOutcome($evidenceId: uuid) {
    outcomes: evidence_outcome(where: { evidence_id: { _eq: $evidenceId } }) {
      id
      outcome {
        id
        title
        description
        type
        overall: overall_outcome
        outcomes {
          id
          title
          overall: overall_outcome
          markings {
            id
            member_id
          }
        }
      }
    }
  }
`;

export const getAssignmentVersion = gql`
  query getAssignmentVersion($unitId: uuid!, $userId: uuid!) {
    evidenceAggregate: evidence_aggregate(
      where: {
        _and: [{ unit_id: { _eq: $unitId } }, { owner_id: { _eq: $userId } }]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const getAssignment = gql`
  query getAssignment($id: uuid!) {
    assignment: evidence_by_pk(id: $id) {
      id
      created_at
      submitted
      submitted_at
      archived
      content
      assignment
      revision
      version
      unit_id
      owner_id
      owner {
        full_name
      }
      unit {
        programme {
          level
        }
      }
      statuses(order_by: { created_at: desc }) {
        id
        status
        created_at
        createdBy {
          id
          full_name
        }
      }
      notes {
        internal
        id
        type
        note {
          created_at
          updated_at
          data
          id
          user
          employee {
            full_name
            avatar: user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
      }
    }
  }
`;

export const getMarkings = gql`
  query getMarkings($evidenceId: uuid!) {
    markings: evidence_marking(where: { evidence_id: { _eq: $evidenceId } }) {
      id
      created_at
      updated_at
      outcome_id
      member_id
      evidence_id
      type
      user {
        full_name
      }
    }
  }
`;

export const getEvidence = gql`
  query Evidence($id: uuid!) {
    evidence: evidence_by_pk(id: $id) {
      id
      created_at
      submitted
      submitted_at
      archived
      assignment
      version
      revision
      revision_at
      accepted
      accepted_at
      uploaded_assignment
      markings {
        id
        created_at
        updated_at
        outcome_id
        member_id
        evidence_id
      }
      outcomes(order_by: { outcome: { outcome_group: asc } }) {
        id
        outcome_id
        outcome {
          id
          description
          title
          type
          group: outcome_group
          overall: overall_outcome
          outcomes(order_by: { overall_outcome: desc }) {
            id
            title
            description
            type
            group: outcome_group
            overall: overall_outcome
            assignment_options
          }
        }
      }
      cohort {
        due_dates
        students: members(
          where: { _and: [{ type: { _eq: "STUDENT" } }] }
          order_by: { created_at: desc }
        ) {
          id
          created_at
          updated_at
          status
          member {
            id
            full_name
          }
        }
        team: members(
          where: {
            _or: [
              { type: { _eq: "TUTOR" } }
              { type: { _eq: "ASSESSOR" } }
              { type: { _eq: "IQA" } }
            ]
          }
        ) {
          id
          created_at
          updated_at
          type
          status
          member {
            id
            full_name
            avatar: user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
      }
      unit {
        title
        description
        active
        unit_code
        assignment
        assignment_template
        e_learning_id
        assets
        questions: outcomes(
          where: { type: { _eq: "KNOWLEDGE" } }
          order_by: { outcome_group: asc }
        ) {
          id
          title
          description
          type
          assignment_options
          overall: overall_outcome
        }
        outcomes(order_by: { outcome_group: asc }) {
          id
          title
          description
          type
          group: outcome_group
          assignment_options
          overall: overall_outcome
        }
      }
      owner {
        id
        full_name
      }
      createdBy {
        id
        full_name
      }
      asset_id
      asset {
        id
        url
        props
      }
      report_id
      report {
        id
        created_at
        event {
          id
          detail {
            id
            title
          }
        }
      }
      form_id
      form {
        id
        created_at
        form: formByForm {
          title
        }
        createdAt: created_at
        updatedAt: updated_at
        formByForm {
          id
          title
          content
          formQuestions(
            where: {
              _and: [
                { questionByQuestion: { archived: { _eq: false } } }
                { archived: { _eq: false } }
              ]
            }
          ) {
            id
            order
            properties
            question: questionByQuestion {
              label
              type
            }
          }
        }
        responsesByResponses {
          id
          complete
          responses
        }
      }
      statuses(order_by: { created_at: desc }) {
        id
        status
        created_at
        createdBy {
          id
          full_name
        }
      }
    }
  }
`;

export const getMemberUnitEvidence = gql`
  query getMemberUnitEvidence(
    $userId: uuid!
    $unitId: uuid!
    $assignment: Boolean!
  ) {
    evidence(
      order_by: { created_at: desc }
      where: {
        _and: [
          { unit_id: { _eq: $unitId } }
          { owner_id: { _eq: $userId } }
          { assignment: { _eq: $assignment } }
        ]
      }
    ) {
      id
      created_at
      archived
      submitted
      submitted_at
      updated_at
      markings {
        updated_at
        member_id
        created_at
        outcome_id
      }
      revision
      unit {
        id
        title
        unit_code
      }
      outcomes {
        id
        outcome_id
        evidence_id
        outcome {
          id
          title
          description
          type
          overall: overall_outcome
          group: outcome_group
        }
      }
      asset_id
      asset {
        id
        url
        props
      }
      report_id
      report {
        id
        created_at
      }
      form_id
      form {
        id
        created_at
        form: formByForm {
          title
        }
      }
      statuses(order_by: { created_at: desc }) {
        id
        status
      }
    }
  }
`;

export const getMemberEvidence = gql`
  query getMemberEvidence($userId: uuid!, $assignment: Boolean!) {
    evidence(
      order_by: { created_at: desc }
      where: {
        _and: [
          { owner_id: { _eq: $userId } }
          { assignment: { _eq: $assignment } }
        ]
      }
    ) {
      id
      created_at
      archived
      submitted
      submitted_at
      updated_at
      revision
      markings {
        updated_at
        outcome_id
      }
      unit {
        id
        title
        unit_code
        outcomes {
          id
        }
      }
      outcomes {
        id
        outcome {
          id
          title
          description
          type
          overall: overall_outcome
          group: outcome_group
        }
      }
      asset_id
      asset {
        id
        url
        props
      }
      report_id
      report {
        id
        created_at
      }
      form_id
      form {
        id
        created_at
        form: formByForm {
          title
        }
      }
      statuses(order_by: { created_at: desc }) {
        id
        status
      }
    }
  }
`;

export const getMemberEvidenceProgramme = gql`
  query getMemberEvidence(
    $userId: uuid!
    $programmeId: uuid!
    $assignment: Boolean!
    $uploaded_assignment: Boolean!
  ) {
    evidence(
      order_by: { updated_at: desc }
      where: {
        _and: [
          { archived: { _neq: true } }
          { unit: { programme_id: { _eq: $programmeId } } }
          { owner_id: { _eq: $userId } }
          { assignment: { _eq: $assignment } }
          { uploaded_assignment: { _eq: $uploaded_assignment } }
        ]
      }
    ) {
      id
      created_at
      archived
      submitted
      submitted_at
      accepted_at
      revision_at
      updated_at
      revision
      markings {
        id
        created_at
        updated_at
        outcome_id
        member_id
        evidence_id
      }
      unit {
        id
        title
        unit_code
        outcomes {
          id
        }
      }
      outcomes {
        id
        outcome {
          id
          title
          description
          type
          overall: overall_outcome
          group: outcome_group
        }
      }
      asset_id
      asset {
        id
        url
        props
      }
      report_id
      report {
        id
        created_at
      }
      form_id
      form {
        id
        created_at
        form: formByForm {
          title
        }
      }
      statuses(order_by: { created_at: desc }) {
        id
        status
      }
    }
  }
`;

export const getMemberAssignmentEvidenceProgramme = gql`
  query getMemberEvidence($userId: uuid!, $programmeId: uuid!) {
    evidence(
      order_by: { updated_at: desc }
      where: {
        _and: [
          { archived: { _neq: true } }
          { unit: { programme_id: { _eq: $programmeId } } }
          { owner_id: { _eq: $userId } }
          {
            _or: [
              { assignment: { _eq: true } }
              { uploaded_assignment: { _eq: true } }
            ]
          }
        ]
      }
    ) {
      id
      created_at
      archived
      uploaded_assignment
      assignment
      submitted
      submitted_at
      accepted_at
      revision_at
      updated_at
      revision
      markings {
        id
        created_at
        updated_at
        outcome_id
        member_id
        evidence_id
      }
      unit {
        id
        title
        unit_code
        outcomes {
          id
        }
      }
      outcomes {
        id
        outcome {
          id
          title
          description
          type
          overall: overall_outcome
          group: outcome_group
        }
      }
      asset_id
      asset {
        id
        url
        props
      }
      report_id
      report {
        id
        created_at
      }
      form_id
      form {
        id
        created_at
        form: formByForm {
          title
        }
      }
      statuses(order_by: { created_at: desc }) {
        id
        status
      }
    }
  }
`;

export const getMember = gql`
  query cohortMember($id: uuid!) {
    registration(
      where: {
        _and: [
          { user: { cohort_members: { id: { _eq: $id } } } }
          { type: { _eq: "STUDENT" } }
        ]
      }
      order_by: { event: { start: asc } }
    ) {
      id
      event_id
      type
      event {
        id
        start
        end
        title
        detail {
          title
        }
        vtctSettings
      }
    }
    cohortMember: cohort_member_by_pk(id: $id) {
      id
      created_at
      updated_at
      tracking_number
      certificate_received_at
      pdf_cert_sent_at
      physical_cert_posted_at
      type
      status
      archived
      complete_at
      removed_at
      certification_at
      exam_scheduling_approved_at
      exam_scheduling_approved_by
      evidence_complete
      event_id
      examSchedulingApprovedBy {
        id
        full_name
      }
      online_registrations {
        id
        workflow_status
        certificate_id
        configuration {
          course_title
          option_label
          option_code
          certificate_accreditation
          certificate_points
          certificate_number_prefix
          certificate_release_mode
        }
      }
      event {
        id
        detail {
          title
        }
        title
        start
        end
        vtctSettings
      }
      member {
        id
        full_name
        email
        vtct_learner_code
        enrollments(
          where: {
            archived: { _eq: false }
            status: { _nin: ["draft", "cancelled", "canceled", "refunded"] }
          }
        ) {
          id
          metadata
          courses {
            event {
              detail_id
              title
              detail {
                title
              }
            }
          }
        }
      }
      user_forms(order_by: { created_at: desc }) {
        id
        created_at
        updated_at
        form: formByForm {
          id
          title
          content
          formQuestions(order_by: { order: asc }) {
            id
            order
            properties
            question: questionByQuestion {
              id
              label
              archived
              type
              public
              properties
            }
          }
        }
        formByForm {
          id
          title
        }
        responsesByResponses {
          id
          code
          complete
          completed_at
          client_complete
          client_completed_at
        }
      }
      addedBy {
        id
        full_name
      }
      notes {
        id
        internal
        note {
          created_at
          id
          title
          content
        }
      }
      cohort {
        id
        name
        status
        due_dates
        team: members(
          where: {
            _or: [
              { type: { _eq: "TUTOR" } }
              { type: { _eq: "ASSESSOR" } }
              { type: { _eq: "IQA" } }
            ]
          }
        ) {
          id
          created_at
          updated_at
          type
          status
          member {
            id
            full_name
            avatar: user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
        programme {
          id
          code
          active
          title
          itec
          level
          created_at
          units {
            id
            created_at
            updated_at
            title
            description
            active
            unit_code
            e_learning_id
            assets
            outcomes {
              id
              title
              description
              type
              overall: overall_outcome
              group: outcome_group
            }
            hasExam
            exams(order_by: { created_at: asc }) {
              id
              title
              active
              is_default
            }
          }
        }
      }
    }
  }
`;

export const getCohortMember = gql`
  query cohortMember($id: uuid!) {
    user: user_by_pk(id: $id) {
      id
      created_at
      updated_at
      id
      evidenceSummary
      full_name
      name: given_name
      surname: family_name
      email
      registrations {
        id
        event {
          title
          detail_id
          detail {
            title
          }
        }
      }
      enrollments(
        where: {
          archived: { _eq: false }
          status: { _nin: ["draft", "cancelled", "canceled", "refunded"] }
        }
      ) {
        id
        metadata
        courses {
          event {
            detail_id
            title
            detail {
              title
            }
          }
        }
      }
      avatar: user_assets(
        order_by: { created_at: desc }
        limit: 1
        where: { assetByAsset: { type: { _eq: AVATAR } } }
      ) {
        asset: assetByAsset {
          id
          url
        }
      }
      assignment_aggregate: evidence_aggregate(
        where: { assignment: { _eq: true } }
      ) {
        aggregate {
          count
        }
      }
      evidence_aggregate(where: { assignment: { _eq: false } }) {
        aggregate {
          count
        }
      }
      cohort_members {
        id
        created_at
        updated_at
        type
        status
        archived
        evidence_complete
        tutor_id
        assessor_id
        iqa_id
        member {
          id
          full_name
        }
        addedBy {
          id
          full_name
        }
        notes {
          id
          internal
          note {
            created_at
            id
            title
            content
          }
        }
        cohort {
          id
          name
          status
          due_dates
          team: members(
            where: {
              _or: [
                { type: { _eq: "TUTOR" } }
                { type: { _eq: "ASSESSOR" } }
                { type: { _eq: "IQA" } }
              ]
            }
          ) {
            id
            created_at
            updated_at
            type
            status
            member {
              id
              full_name
              avatar: user_assets(
                order_by: { created_at: desc }
                limit: 1
                where: { assetByAsset: { type: { _eq: AVATAR } } }
              ) {
                asset: assetByAsset {
                  id
                  url
                }
              }
            }
          }
          programme {
            id
            code
            active
            title
            created_at
            units {
              id
              created_at
              updated_at
              title
              description
              active
              unit_code
              e_learning_id
              assets
            }
          }
        }
      }
    }
  }
`;

export const cohortMember = gql`
  query CohortMembers($id: uuid!) {
    cohortMember: cohort_member_by_pk(id: $id) {
      id
      created_at
      updated_at
      type
      status
      notes {
        id
        internal
        note {
          created_at
          id
          title
          content
        }
      }
      cohort {
        id
        name
        status
        due_dates
        team: members(
          where: {
            _or: [
              { type: { _eq: "TUTOR" } }
              { type: { _eq: "ASSESSOR" } }
              { type: { _eq: "IQA" } }
            ]
          }
        ) {
          id
          created_at
          updated_at
          type
          status
          member {
            id
            full_name
            avatar: user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
        programme {
          code
          title
          units {
            id
            created_at
            updated_at
            title
            description
            active
            unit_code
            e_learning_id
            assets
          }
        }
      }
      assignments {
        id
        created_at
        statuses {
          id
          created_at
          status
        }
        unit {
          title
          description
          active
          unit_code
          e_learning_id
          assets
        }
      }
      member {
        id
        full_name
        avatar: user_assets {
          asset: assetByAsset {
            id
            url
          }
        }
      }
    }
  }
`;

export const insertCohortNote = gql`
  mutation insertCohortNote($data: cohort_note_insert_input!) {
    note: insert_cohort_note_one(object: $data) {
      id
    }
  }
`;

export const insertEvidenceStatus = gql`
  mutation insertEvidenceStatus($data: [evidence_status_insert_input!]!) {
    status: insert_evidence_status(objects: $data) {
      affected_rows
    }
  }
`;

export const insertEvidenceMarking = gql`
  mutation insertEvidenceMarking($data: evidence_marking_insert_input!) {
    marking: insert_evidence_marking_one(object: $data) {
      id
    }
  }
`;

export const insertEvidenceMarkings = gql`
  mutation insertEvidenceMarkings($data: [evidence_marking_insert_input!]!) {
    marking: insert_evidence_marking(objects: $data) {
      affected_rows
    }
  }
`;

export const deleteEvidenceMarkings = gql`
  mutation deleteEvidenceMarkings($where: evidence_marking_bool_exp!) {
    marking: delete_evidence_marking(where: $where) {
      affected_rows
    }
  }
`;

export const deleteEvidenceOutcomes = gql`
  mutation deleteEvidenceOutcomes($where: evidence_outcome_bool_exp!) {
    outcome: delete_evidence_outcome(where: $where) {
      affected_rows
    }
  }
`;

export const insertEvidenceOutcomes = gql`
  mutation insertEvidenceOutcomes($data: [evidence_outcome_insert_input!]!) {
    insert_evidence_outcome(objects: $data) {
      affected_rows
    }
  }
`;

export const deleteEvidenceMarking = gql`
  mutation deleteEvidenceMarking($id: uuid!) {
    marking: delete_evidence_marking_by_pk(id: $id) {
      id
    }
  }
`;

export const deleteEvidenceNote = gql`
  mutation deleteEvidenceNote($id: uuid!) {
    note: delete_evidence_note_by_pk(id: $id) {
      id
    }
  }
`;

export const addEvidenceNote = gql`
  mutation addEvidenceNote($data: evidence_note_insert_input!) {
    insert_evidence_note_one(object: $data) {
      id
    }
  }
`;

export const insertEvidence = gql`
  mutation insertEvidence($data: evidence_insert_input!) {
    insert_evidence_one(object: $data) {
      id
    }
  }
`;

export const insertEvidences = gql`
  mutation insertEvidences($data: [evidence_insert_input!]!) {
    insert_evidence(objects: $data) {
      returning {
        id
      }
    }
  }
`;

export const insertEvidenceOutcome = gql`
  mutation insertEvidenceOutcome($data: evidence_outcome_insert_input!) {
    insert_evidence_outcome_one(object: $data) {
      id
    }
  }
`;

export const insertCohortMemberNote = gql`
  mutation insertCohortMemberNote($data: cohort_member_note_insert_input!) {
    note: insert_cohort_member_note_one(object: $data) {
      id
    }
  }
`;

export const deleteCohortMemberNote = gql`
  mutation deleteCohortMemberNote($id: uuid!) {
    note: delete_cohort_member_note_by_pk(id: $id) {
      id
    }
  }
`;

export const deleteCohortNote = gql`
  mutation deleteCohortNote($id: uuid!) {
    note: delete_cohort_note_by_pk(id: $id) {
      id
    }
  }
`;

export const insertUnit = gql`
  mutation CreateUnit($data: programme_unit_insert_input!) {
    insert_programme_unit_one(object: $data) {
      id
    }
  }
`;

export const insertOutcome = gql`
  mutation CreateOutcome($data: programme_unit_outcome_insert_input!) {
    insert_programme_unit_outcome_one(object: $data) {
      id
    }
  }
`;

export const updateProgramme = gql`
  mutation updateProgramme($id: uuid!, $data: programme_set_input!) {
    update_programme_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const updateUnit = gql`
  mutation updateUnit($id: uuid!, $data: programme_unit_set_input!) {
    update_programme_unit_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const updateOutcome = gql`
  mutation updateOutcome($id: uuid!, $data: programme_unit_outcome_set_input!) {
    update_programme_unit_outcome_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const deleteUnit = gql`
  mutation deletUnit($id: uuid!) {
    delete_programme_unit_by_pk(id: $id) {
      id
    }
  }
`;

export const deleteOutcome = gql`
  mutation deletOutcome($id: uuid!) {
    delete_programme_unit_outcome_by_pk(id: $id) {
      id
    }
  }
`;

export const getProgrammeFromCohortID = gql`
  query getProgrammeFromCohortID($id: uuid!) {
    programme(where: { cohorts: { id: { _eq: $id } } }) {
      id
    }
  }
`;

export const getProgrammeFromUnitID = gql`
  query getProgrammeFromUnitID($id: uuid!) {
    programme(where: { units: { id: { _eq: $id } } }) {
      id
    }
  }
`;

export const getCohortMemberByID = gql`
  query getCohortMemberByID($id: uuid!) {
    members: user(
      order_by: { given_name: asc }
      where: {
        _and: [
          { cohort_members: { id: { _eq: $id } } }
          { cohort_members: { type: { _eq: "STUDENT" } } }
        ]
      }
    ) {
      id
      email
      programmeStatus
      created_at
      updated_at
      full_name
      name: given_name
      surname: family_name
      cohort_members {
        cohort_id
        cohort {
          id
          programme_id
          programme {
            title
          }
          event_id
          team: members(
            where: {
              _or: [
                { type: { _eq: "TUTOR" } }
                { type: { _eq: "ASSESSOR" } }
                { type: { _eq: "IQA" } }
              ]
            }
          ) {
            id
            type
          }
        }
      }
    }
  }
`;

export const getStudentFromEvidenceID = gql`
  query getStudentFromEvidenceID($id: uuid!) {
    evidence_by_pk(id: $id) {
      owner_id
    }
  }
`;

export const getPendingAssignmentStats = gql`
  query AssignmentDetails {
    all: evidence_aggregate(
      where: {
        _and: [
          {
            cohortMember: {
              type: { _eq: "STUDENT" }
              status: { _neq: "REMOVED" }
            }
          }
          {
            _or: [
              { assignment: { _eq: true } }
              { uploaded_assignment: { _eq: true } }
            ]
          }
          { submitted: { _eq: true } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    complete: evidence_aggregate(
      where: {
        _and: [
          {
            cohortMember: {
              type: { _eq: "STUDENT" }
              status: { _neq: "REMOVED" }
            }
          }
          {
            _or: [
              { assignment: { _eq: true } }
              { uploaded_assignment: { _eq: true } }
            ]
            submitted: { _eq: true }
            accepted: { _eq: true }
          }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    pending: evidence_aggregate(
      where: {
        _and: [
          {
            cohortMember: {
              type: { _eq: "STUDENT" }
              status: { _neq: "REMOVED" }
            }
          }
          {
            _or: [
              { assignment: { _eq: true } }
              { uploaded_assignment: { _eq: true } }
            ]
          }
          { accepted: { _eq: false } }
          { submitted: { _eq: true } }
          { revision: { _eq: false } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const getPendingAssignments = gql`
  query AssignmentDetails($limit: Int = 20, $offset: Int = 0) {
    assignments: evidence(
      where: {
        _and: [
          {
            cohortMember: {
              type: { _eq: "STUDENT" }
              status: { _neq: "REMOVED" }
            }
          }
          {
            _or: [
              { assignment: { _eq: true } }
              { uploaded_assignment: { _eq: true } }
            ]
          }
          { accepted: { _eq: false } }
          { submitted: { _eq: true } }
          { revision: { _eq: false } }
          { _not: { statuses: {} } }
        ]
      }
      limit: $limit
      offset: $offset
    ) {
      id
      archived
      submitted
      submitted_at
      revision
      markings {
        updated_at
        outcome_id
      }
      outcomesAggregate: outcomes_aggregate {
        aggregate {
          count
        }
      }
      unit {
        id
        title
        unit_code
        outcomes {
          id
        }
      }
      owner {
        id
        name: given_name
        surname: family_name
      }
      statuses {
        id
        status
      }
      created_at
    }
  }
`;

export const getPendingEvidence = gql`
  query AssignmentDetails($limit: Int = 20, $offset: Int = 0) {
    assignments: evidence(
      where: {
        _and: [
          {
            cohortMember: {
              type: { _eq: "STUDENT" }
              status: { _neq: "REMOVED" }
            }
          }
          { assignment: { _eq: false } }
          { uploaded_assignment: { _eq: false } }
          { accepted: { _eq: false } }
          { submitted: { _eq: true } }
          { revision: { _eq: false } }
        ]
      }
      limit: $limit
      offset: $offset
    ) {
      id
      archived
      submitted
      submitted_at
      revision
      markings {
        updated_at
        outcome_id
      }
      outcomesAggregate: outcomes_aggregate {
        aggregate {
          count
        }
      }
      unit {
        id
        title
        unit_code
        outcomes {
          id
        }
        programme {
          id
          title
        }
      }
      owner {
        id
        name: given_name
        surname: family_name
      }
      statuses {
        id
        status
      }
      created_at
    }
  }
`;

export const getEvidenceList = gql`
  query AssignmentDetails(
    $where: evidence_bool_exp
    $limit: Int = 20
    $offset: Int = 0
    $order_by: [evidence_order_by!]
  ) {
    assignments: evidence(
      where: $where
      limit: $limit
      offset: $offset
      order_by: $order_by
    ) {
      id
      archived
      submitted
      submitted_at
      revision
      markings {
        updated_at
        outcome_id
      }
      outcomesAggregate: outcomes_aggregate {
        aggregate {
          count
        }
      }
      unit {
        id
        title
        unit_code
        outcomes {
          id
        }
        programme {
          id
          title
        }
      }
      owner {
        id
        name: given_name
        surname: family_name
      }
      statuses {
        id
        status
      }
      created_at
    }
  }
`;

export const getPendingEvidenceStats = gql`
  query AssignmentDetails($where: evidence_bool_exp) {
    all: evidence_aggregate(where: $where) {
      aggregate {
        count
      }
    }
    complete: evidence_aggregate(
      where: {
        _and: [
          {
            cohortMember: {
              type: { _eq: "STUDENT" }
              status: { _neq: "REMOVED" }
            }
          }
          {
            assignment: { _eq: false }
            uploaded_assignment: { _eq: false }
            submitted: { _eq: true }
            accepted: { _eq: true }
          }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    pending: evidence_aggregate(
      where: {
        _and: [
          {
            cohortMember: {
              type: { _eq: "STUDENT" }
              status: { _neq: "REMOVED" }
            }
          }
          { assignment: { _eq: false } }
          { uploaded_assignment: { _eq: false } }
          { accepted: { _eq: false } }
          { submitted: { _eq: true } }
          { revision: { _eq: false } }
          { archived: { _eq: false } }
          { _not: { statuses: { status: { _eq: COMPLETE } } } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const getEvidenceAgeingStats = gql`
  query EvidenceAgeingStats(
    $where: evidence_bool_exp
    $over10: timestamptz!
    $over15: timestamptz!
    $over20: timestamptz!
  ) {
    over10: evidence_aggregate(
      where: {
        _and: [
          $where
          { submitted: { _eq: true } }
          { accepted: { _eq: false } }
          { revision: { _eq: false } }
          { archived: { _eq: false } }
          { submitted_at: { _is_null: false, _lte: $over10 } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    over15: evidence_aggregate(
      where: {
        _and: [
          $where
          { submitted: { _eq: true } }
          { accepted: { _eq: false } }
          { revision: { _eq: false } }
          { archived: { _eq: false } }
          { submitted_at: { _is_null: false, _lte: $over15 } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    over20: evidence_aggregate(
      where: {
        _and: [
          $where
          { submitted: { _eq: true } }
          { accepted: { _eq: false } }
          { revision: { _eq: false } }
          { archived: { _eq: false } }
          { submitted_at: { _is_null: false, _lte: $over20 } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const getEvidenceAgeingRows = gql`
  query EvidenceAgeingRows($where: evidence_bool_exp) {
    items: evidence(where: $where) {
      id
      submitted
      submitted_at
      accepted
      revision
      archived
      assignment
      uploaded_assignment
      owner {
        full_name
      }
      unit {
        title
        programme {
          title
        }
      }
    }
  }
`;

export const getEvidenceNotes = gql`
  query getEvidenceNotes(
    $where: evidence_note_bool_exp!
    $limit: Int
    $offset: Int
    $order_by: [evidence_note_order_by!]
  ) {
    notes: evidence_note(
      where: $where
      limit: $limit
      offset: $offset
      order_by: $order_by
    ) {
      internal
      id
      type
      note {
        created_at
        updated_at
        data
        id
        user
        employee {
          full_name
          avatar: user_assets {
            asset: assetByAsset {
              id
              url
            }
          }
        }
      }
    }
  }
`;

export const getTrackingDataCounts = gql`
  query TrackingQueryCounts(
    $programmeId: uuid!
    $includeRpl: Boolean! = false
    $rplWhere: cohort_member_bool_exp
  ) {
    complete: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { cohort: { programme_id: { _eq: $programmeId } } }
          { status: { _eq: "COMPLETE" } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    removed: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { cohort: { programme_id: { _eq: $programmeId } } }
          { status: { _eq: "REMOVED" } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    claimed: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { cohort: { programme_id: { _eq: $programmeId } } }
          { status: { _eq: "CERTIFICATION_CLAIMED" } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    certificateReceived: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { cohort: { programme_id: { _eq: $programmeId } } }
          { status: { _eq: "CERTIFICATE_RECEIVED" } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    iqa: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { cohort: { programme_id: { _eq: $programmeId } } }
          { status: { _eq: "IQA" } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    assessed: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { cohort: { programme_id: { _eq: $programmeId } } }
          { status: { _eq: "ASSESSED" } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    current: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { cohort: { programme_id: { _eq: $programmeId } } }
          { status: { _eq: "ACTIVE" } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    rpl: cohort_member_aggregate(where: $rplWhere) @include(if: $includeRpl) {
      aggregate {
        count
      }
    }
  }
`;

export const getTrackingAllDataCounts = gql`
  query TrackingQueryCounts {
    complete: cohort_member_aggregate(
      where: {
        _and: [{ type: { _eq: "STUDENT" } }, { status: { _eq: "COMPLETE" } }]
      }
    ) {
      aggregate {
        count
      }
    }
    removed: cohort_member_aggregate(
      where: {
        _and: [{ type: { _eq: "STUDENT" } }, { status: { _eq: "REMOVED" } }]
      }
    ) {
      aggregate {
        count
      }
    }
    claimed: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { status: { _eq: "CERTIFICATION_CLAIMED" } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    certificateReceived: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { status: { _eq: "CERTIFICATE_RECEIVED" } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    iqa: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { status: { _eq: "IQA" } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    assessed: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { status: { _eq: "ASSESSED" } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    current: cohort_member_aggregate(
      where: {
        _and: [
          { type: { _eq: "STUDENT" } }
          { status: { _eq: "ACTIVE" } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const getTrackingData = gql`
  query TrackingQuery(
    $where: cohort_member_bool_exp
    $order_by: [cohort_member_order_by!]
    $limit: Int
    $offset: Int
    $programmeId: uuid
  ) {
    rplEventDetails: event_detail(
      where: {
        _and: [
          { programme_id: { _eq: $programmeId } }
          { title: { _ilike: "%(RPL)%" } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      id
    }
    students: cohort_member(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      id
      created_at
      updated_at
      status
      archived
      tracking_number
      pdf_cert_sent_at
      physical_cert_posted_at
      exam_scheduling_approved_at
      tutor_id
      assessor_id
      iqa_id
      event {
        id
        start
        detail {
          title
          programme_id
        }
      }
      member {
        id
        full_name
        vtct_learner_code
        enrollments(
          where: {
            archived: { _eq: false }
            status: { _nin: ["draft", "cancelled", "canceled", "refunded"] }
          }
        ) {
          id
          metadata
        }
        registrations(
          where: { event: { detail: { programme_id: { _eq: $programmeId } } } }
        ) {
          event {
            id
            start
            vtctSettings
            detail {
              title
            }
          }
        }
        evidence(
          where: {
            _and: [
              { submitted: { _eq: true } }
              { revision: { _eq: false } }
              { accepted: { _eq: true } }
            ]
          }
        ) {
          id
          assignment
          uploaded_assignment
          markings(where: { type: { _eq: "ASSESSOR" } }) {
            outcome_id
            outcome {
              unit: programme_unit_id
            }
          }
        }
        all_evidence: evidence(
          where: {
            _or: [
              { assignment: { _eq: true } }
              { uploaded_assignment: { _eq: true } }
            ]
          }
        ) {
          id
          assignment
          uploaded_assignment
          submitted
        }
      }
      cohort {
        id
        event {
          id
          start
        }
        team: members(
          where: {
            _or: [{ type: { _eq: "ASSESSOR" } }, { type: { _eq: "IQA" } }]
          }
        ) {
          id
          member_id
          type
        }
        programme {
          id
          title
          code
          level
        }
      }
    }
  }
`;

export const getProgrammeEvidenceOutomes = gql`
  query getProgrammeEvidence($userId: uuid!, $programmeId: uuid!) {
    evidence(
      where: {
        _and: [
          { owner_id: { _eq: $userId } }
          { unit: { programme_id: { _eq: $programmeId } } }
        ]
      }
    ) {
      id
      created_at
      assignment
      updated_at
      revision
      submitted
      markings {
        updated_at
        outcome_id
      }
      outcomes {
        outcome_id
        evidence_id
      }
    }
  }
`;

export const getUnitEvidenceOutomes = gql`
  query getUnitEvidenceOutomes($userId: uuid!, $unitId: uuid!) {
    evidence(
      where: {
        _and: [{ unit_id: { _eq: $unitId } }, { owner_id: { _eq: $userId } }]
      }
    ) {
      id
      created_at
      assignment
      outcomes {
        outcome_id
        evidence_id
      }
    }
  }
`;

export const Markings = gql`
  query Markings($userId: uuid, $unitId: uuid!) {
    evidence_marking(
      where: {
        _and: [
          {
            _and: [
              { evidence: { archived: { _eq: false } } }
              { evidence: { accepted: { _eq: true } } }
              { evidence: { owner_id: { _eq: $userId } } }
            ]
          }
          { outcome: { programme_unit_id: { _eq: $unitId } } }
        ]
      }
    ) {
      id
      created_at
      updated_at
      outcome_id
      member_id
      evidence_id
      evidence {
        archived
      }
      user {
        full_name
      }
      type
      markedBy {
        id
      }
    }
  }
`;

export const MarkingsProgramme = gql`
  query Markings($userId: uuid, $programmeId: uuid!) {
    evidence_marking(
      where: {
        _and: [
          { evidence: { owner_id: { _eq: $userId } } }
          { evidence: { unit: { programme_id: { _eq: $programmeId } } } }
        ]
      }
    ) {
      id
      created_at
      updated_at
      outcome_id
      member_id
      evidence_id
      type
      user {
        full_name
      }
      markedBy {
        id
      }
    }
  }
`;

export const getEvents = gql`
  query getEvents($start: timestamptz!, $end: timestamptz!, $userId: uuid) {
    events: get_events_with_status(
      args: { period_end: $end, period_start: $start, event_status: "NEW" }
      order_by: { start: desc }
      where: { registrations: { user_id: { _eq: $userId } } }
    ) {
      id
      type
      start
      end
      vtctSettings
      detail {
        title
        programme_id
        category
      }
    }
  }
`;
